// Pure geometry for room polygon shapes on the floor plan (Andar.360).
// All coordinates are percentages (0-100) of the rendered floor-plan image —
// the same space as `rooms.map_pos`. Kept DOM-free and side-effect-free so the
// draw hook (`useShapeEditor`), the viewer render and the label-anchor logic all
// share one tested source.
import type { MapPosition2D } from "@/types";

/** A polygon is renderable/closable only with at least 3 vertices. */
export const isClosablePolygon = (pts: MapPosition2D[] | null | undefined) =>
  !!pts && pts.length >= 3;

/** Clamp a percent coordinate into the image bounds. */
export const clampPct = (v: number) => Math.max(0, Math.min(100, v));

/** Squared distance in % space (avoids the sqrt for threshold checks). */
const dist2 = (a: MapPosition2D, b: MapPosition2D) => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};

/**
 * True when `p` is within `thresholdPct` of `target` — used to detect
 * "tapped the first vertex to close the polygon" and vertex hit-testing.
 */
export const nearVertex = (
  p: MapPosition2D,
  target: MapPosition2D,
  thresholdPct: number,
) => dist2(p, target) <= thresholdPct * thresholdPct;

/** Arithmetic mean of the vertices — the degenerate fallback for centroid. */
const meanPoint = (pts: MapPosition2D[]): MapPosition2D => {
  const s = pts.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), {
    x: 0,
    y: 0,
  });
  return { x: s.x / pts.length, y: s.y / pts.length };
};

/**
 * Area-weighted polygon centroid (the visual center, robust to uneven vertex
 * spacing). Falls back to the arithmetic mean when the polygon is degenerate
 * (near-zero area: collinear points or < 3 vertices).
 */
export const polygonCentroid = (pts: MapPosition2D[]): MapPosition2D => {
  if (pts.length < 3) return meanPoint(pts);
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const cross = a.x * b.y - b.x * a.y;
    area += cross;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
  }
  area /= 2;
  if (Math.abs(area) < 1e-6) return meanPoint(pts);
  return { x: cx / (6 * area), y: cy / (6 * area) };
};

/**
 * The label/marker anchor for a room: the explicit `map_pos` if set, otherwise
 * the shape's centroid, otherwise null (room not on the plan).
 */
export const shapeDisplayPoint = (
  mapPos: MapPosition2D | null | undefined,
  shape: MapPosition2D[] | null | undefined,
): MapPosition2D | null => {
  if (mapPos) return mapPos;
  if (isClosablePolygon(shape)) return polygonCentroid(shape!);
  return null;
};

/** Serialize a polygon to an SVG `points` string in the 0-100 viewBox. */
/** Axis-aligned bounding box of a polygon, in the same % space as the points. */
export const polygonBBox = (pts: MapPosition2D[]) => {
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return {
    minX,
    minY,
    maxX,
    maxY,
    w: maxX - minX,
    h: maxY - minY,
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2,
  };
};

export const toSvgPoints = (pts: MapPosition2D[]): string =>
  pts.map((p) => `${p.x},${p.y}`).join(" ");

/** Absolute polygon area in %² (shoelace). */
export const polygonArea = (pts: MapPosition2D[]): number => {
  if (pts.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
};

/**
 * Meters per %-unit, derived from the room's known real area (`mts2`) and the
 * polygon's area in % space: `k = sqrt(mts2 / areaPct)`. Lets us label edge
 * lengths without a separate scale calibration. APPROXIMATE — assumes the %
 * space is isometric (image not stretched vs real proportions). null when it
 * can't be derived (no area / no mts2).
 */
export const metersPerUnit = (
  pts: MapPosition2D[],
  mts2: number | null | undefined,
): number | null => {
  if (!mts2 || mts2 <= 0) return null;
  const area = polygonArea(pts);
  if (area <= 0) return null;
  return Math.sqrt(mts2 / area);
};

/** Distance in % space between two points. */
const dist = (a: MapPosition2D, b: MapPosition2D) =>
  Math.hypot(a.x - b.x, a.y - b.y);

/** Midpoint of every edge of a (closed) polygon, tagged with its edge index —
 *  used to place the "insert vertex here" handles. */
export const edgeMidpoints = (
  pts: MapPosition2D[],
): { edgeIdx: number; mid: MapPosition2D }[] =>
  pts.map((p, i) => {
    const q = pts[(i + 1) % pts.length];
    return { edgeIdx: i, mid: { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 } };
  });

/** Return a new polygon with `p` inserted right after `edgeIdx`. */
export const insertVertexAfter = (
  pts: MapPosition2D[],
  edgeIdx: number,
  p: MapPosition2D,
): MapPosition2D[] => {
  const next = [...pts];
  next.splice(edgeIdx + 1, 0, p);
  return next;
};

/** Closed-polygon perimeter in % units. */
export const polygonPerimeter = (pts: MapPosition2D[]): number => {
  if (pts.length < 2) return 0;
  let s = 0;
  for (let i = 0; i < pts.length; i++) s += dist(pts[i], pts[(i + 1) % pts.length]);
  return s;
};

/** Perimeter in real meters (via the mts2-derived scale). null if underivable. */
export const perimeterMeters = (
  pts: MapPosition2D[],
  mts2: number | null | undefined,
): number | null => {
  const k = metersPerUnit(pts, mts2);
  return k == null ? null : polygonPerimeter(pts) * k;
};

/** Magnet: if `p` is within `thresholdPct` of any vertex, snap onto it. */
export const snapToNearbyVertex = (
  p: MapPosition2D,
  vertices: MapPosition2D[],
  thresholdPct: number,
): MapPosition2D => {
  for (const v of vertices) if (nearVertex(p, v, thresholdPct)) return { ...v };
  return p;
};

/**
 * Snap the segment `prev`→`p` to the nearest `stepDeg` multiple (45° by default:
 * straight/diagonal walls) when within `toleranceDeg`, keeping the length.
 */
export const snapAngle = (
  prev: MapPosition2D,
  p: MapPosition2D,
  toleranceDeg = 8,
  stepDeg = 45,
): MapPosition2D => {
  const dx = p.x - prev.x;
  const dy = p.y - prev.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return p;
  const ang = (Math.atan2(dy, dx) * 180) / Math.PI;
  const snapped = Math.round(ang / stepDeg) * stepDeg;
  if (Math.abs(ang - snapped) > toleranceDeg) return p;
  const r = (snapped * Math.PI) / 180;
  return {
    x: clampPct(prev.x + Math.cos(r) * len),
    y: clampPct(prev.y + Math.sin(r) * len),
  };
};

/** Direction of edge `i` (p[i]→p[i+1]) as an angle in [0,180) degrees — walls
 *  that point opposite ways still count as the same orientation. */
const edgeAngle = (pts: MapPosition2D[], i: number): number => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  const d = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  return ((d % 180) + 180) % 180;
};

/** Indices of the edges parallel to `edgeIdx` (within `tolDeg`), excluding it.
 *  Used by the "cheap" resize: setting one side length nudges its parallels. */
export const edgesParallelTo = (
  pts: MapPosition2D[],
  edgeIdx: number,
  tolDeg = 3,
): number[] => {
  if (pts.length < 3) return [];
  const base = edgeAngle(pts, edgeIdx);
  const out: number[] = [];
  for (let i = 0; i < pts.length; i++) {
    if (i === edgeIdx) continue;
    let diff = Math.abs(edgeAngle(pts, i) - base);
    diff = Math.min(diff, 180 - diff);
    if (diff <= tolDeg) out.push(i);
  }
  return out;
};

/**
 * Per-edge measures of a CLOSED polygon: midpoint (for label placement) + real
 * length in meters (via `metersPerUnit`). null when the scale can't be derived.
 */
export const edgeMeasures = (
  pts: MapPosition2D[],
  mts2: number | null | undefined,
): { mid: MapPosition2D; meters: number }[] | null => {
  if (!isClosablePolygon(pts)) return null;
  const k = metersPerUnit(pts, mts2);
  if (k == null) return null;
  return pts.map((p, i) => {
    const q = pts[(i + 1) % pts.length];
    return {
      mid: { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 },
      meters: dist(p, q) * k,
    };
  });
};

/** Declared real length (meters) per polygon edge, aligned to edge index
 *  (index i = edge vertex i → i+1). null/undefined = that side not declared. */
export type EdgeLengths = (number | null | undefined)[];

/**
 * Scale (meters per %-unit) fitted from the OWNER-DECLARED side lengths instead
 * of a known area. Several declared edges over-determine one scale, so we take
 * the least-squares fit that minimises Σ(k·pctᵢ − metersᵢ)²  →  k = Σ(pᵢ·Lᵢ)/Σ(pᵢ²).
 * This is the plan-side counterpart of the viewer's `solveSketch` (declared
 * lengths → metric scale). null when no usable declared edge exists.
 */
export const metersPerUnitFromEdges = (
  pts: MapPosition2D[],
  edgeLengths: EdgeLengths | null | undefined,
): number | null => {
  if (!edgeLengths || pts.length < 2) return null;
  let num = 0;
  let den = 0;
  for (let i = 0; i < pts.length; i++) {
    const L = edgeLengths[i];
    if (L == null || L <= 0) continue;
    const p = dist(pts[i], pts[(i + 1) % pts.length]);
    if (p <= 0) continue;
    num += p * L;
    den += p * p;
  }
  return den > 0 ? num / den : null;
};

/**
 * Room area in m² derived from the declared side lengths + the drawn shape:
 * `area = polygonArea(%) · k²` with `k` from `metersPerUnitFromEdges`. This is
 * what the plan writes back into `rooms.mts2` on save. APPROXIMATE — side
 * lengths don't fully determine a polygon (a 4×3 rectangle vs parallelogram
 * share sides, differ in area), so the fit assumes the drawn shape is roughly
 * to proportion. null when scale can't be derived or the polygon isn't closable.
 */
/** Room m² to SHOW: derived live from the declared sides when they give one
 *  (a stored mts2 can be stale — older formula, or sides retyped), else the
 *  stored value. Single source for every surface that prints a room's m². */
export const roomAreaM2 = (
  shape: MapPosition2D[] | null | undefined,
  edgeLengths: EdgeLengths | null | undefined,
  mts2: number | null | undefined,
): number | null => {
  const a = shape ? areaFromEdges(shape, edgeLengths) : null;
  return a != null ? Number(a.toFixed(2)) : (mts2 ?? null);
};

export const areaFromEdges = (
  pts: MapPosition2D[],
  edgeLengths: EdgeLengths | null | undefined,
): number | null => {
  if (!isClosablePolygon(pts)) return null;
  const k = metersPerUnitFromEdges(pts, edgeLengths);
  if (k == null) return null;
  // Walk the drawn edge directions with the DECLARED lengths (undeclared sides
  // keep their scaled drawn length), then shoelace — so a drawn rectangle with
  // sides 2.72/2.22 gives exactly 6.04 m², independent of the drawn proportion.
  // ponytail: closure gap from inconsistent sides is ignored (shoelace closes it).
  const walked: MapPosition2D[] = [{ x: 0, y: 0 }];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const d = dist(a, b);
    const L = edgeLengths?.[i];
    const len = L != null && L > 0 ? L : d * k;
    const prev = walked[walked.length - 1];
    walked.push(
      d > 0
        ? { x: prev.x + ((b.x - a.x) / d) * len, y: prev.y + ((b.y - a.y) / d) * len }
        : prev,
    );
  }
  return polygonArea(walked);
};

/**
 * Midpoint labels for the OWNER-DECLARED side lengths only — the shown value
 * IS `edgeLengths[i]`, never derived from scale/area. Undeclared edges are
 * skipped (no label). Index i = edge vertex i → i+1.
 */
export const declaredEdgeMeasures = (
  pts: MapPosition2D[],
  edgeLengths: EdgeLengths | null | undefined,
): { mid: MapPosition2D; meters: number; edgeIdx: number }[] => {
  if (!isClosablePolygon(pts) || !edgeLengths) return [];
  const out: { mid: MapPosition2D; meters: number; edgeIdx: number }[] = [];
  for (let i = 0; i < pts.length; i++) {
    const L = edgeLengths[i];
    if (L == null || L <= 0) continue;
    const q = pts[(i + 1) % pts.length];
    out.push({
      mid: { x: (pts[i].x + q.x) / 2, y: (pts[i].y + q.y) / 2 },
      meters: L,
      edgeIdx: i,
    });
  }
  return out;
};

/** Even-odd ray cast. Points exactly on an edge may land either side — fine
 *  for label placement, don't use for geometry that needs boundary rules. */
export const pointInPolygon = (
  p: MapPosition2D,
  pts: MapPosition2D[],
): boolean => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i];
    const b = pts[j];
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
    )
      inside = !inside;
  }
  return inside;
};

/** X-intervals where the horizontal line `y` crosses INSIDE the polygon
 *  (even-odd pairing of sorted edge intersections). */
const scanlineIntervals = (
  pts: MapPosition2D[],
  y: number,
): [number, number][] => {
  const xs: number[] = [];
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i];
    const b = pts[j];
    if (a.y > y !== b.y > y)
      xs.push(((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x);
  }
  xs.sort((m, n) => m - n);
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < xs.length; i += 2) out.push([xs[i], xs[i + 1]]);
  return out;
};

/**
 * Anchor for a room's name/m² label: the centroid, pulled inside the visible
 * `viewport` (when the plan is zoomed) AND inside the polygon itself. The old
 * bbox clamp broke on L/U-shaped rooms — clamping into bbox∩viewport can land
 * in the notch, outside the room. Here, if the clamped candidate falls outside
 * the polygon, a few horizontal scanlines find the interior slice closest to
 * the centroid. Returns null when the polygon has NO visible slice in the
 * viewport (bbox overlaps but the shape doesn't) — the caller skips the label.
 * `viewport: null` (unzoomed / minimap) still guarantees an interior point.
 */
export const interiorLabelPoint = (
  pts: MapPosition2D[],
  viewport: { minX: number; minY: number; maxX: number; maxY: number } | null,
  pad = 2,
): MapPosition2D | null => {
  const c = polygonCentroid(pts);
  const b = polygonBBox(pts);
  const clamp = (v: number, lo: number, hi: number) =>
    hi < lo ? (lo + hi) / 2 : Math.max(lo, Math.min(hi, v));
  const xLo = Math.max(b.minX + pad, viewport ? viewport.minX + pad : -Infinity);
  const xHi = Math.min(b.maxX - pad, viewport ? viewport.maxX - pad : Infinity);
  const yLo = Math.max(b.minY + pad, viewport ? viewport.minY + pad : -Infinity);
  const yHi = Math.min(b.maxY - pad, viewport ? viewport.maxY - pad : Infinity);
  const candidate = { x: clamp(c.x, xLo, xHi), y: clamp(c.y, yLo, yHi) };
  if (pointInPolygon(candidate, pts)) return candidate;
  // Candidate outside the shape (L/U notch): scan a handful of rows across the
  // visible y-range, nearest-to-centroid first, and take the interior interval
  // closest to the centroid's x.
  const span = Math.max(0, yHi - yLo);
  const rows = [candidate.y, ...[0.5, 0.25, 0.75, 0.1, 0.9].map((f) => yLo + span * f)];
  for (const y of rows) {
    if (y < yLo || y > yHi) continue;
    let best: MapPosition2D | null = null;
    let bestDist = Infinity;
    for (const [a, z] of scanlineIntervals(pts, y)) {
      const lo = Math.max(a, xLo);
      const hi = Math.min(z, xHi);
      if (hi - lo < 1) continue; // sliver — a label can't sit on it
      const x = clamp(c.x, lo + 0.5, hi - 0.5);
      const d = Math.abs(x - c.x);
      if (d < bestDist) {
        bestDist = d;
        best = { x, y };
      }
    }
    if (best) return best;
  }
  // No interior slice in the viewport at all (bbox overlaps, shape doesn't).
  // Zoomed: hide the label. Unzoomed there is always a slice — but degenerate
  // shapes (zero area) can get here, so keep the centroid as last resort.
  return viewport ? null : candidate;
};
